import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { toast } from "sonner";
import type { UIAudit } from "@/packages/types";
import type { Id } from "@/packages/types";
import type { ValidationReport } from "@/packages/application";
import { Dropdown } from "@/components/ui/Dropdown";
import {
  AUDIT_TRANSITION_CONFIG,
  getActionForTransition,
} from "@/components/audits/constants";
import { useTranslation } from "@/packages/utils";
import { useAuditStatusLabels } from "./useAuditStatusLabels";
import { useAuditTransitionLabels } from "./useAuditTransitionLabels";
import { useAuditStatusChange } from "./use-audit-status-change";
import { AuditStatusConfirmModal } from "./AuditStatusConfirmModal";
import { AuditRejectModal } from "./AuditRejectModal";
import { AuditSubmissionBlockersModal } from "./AuditSubmissionBlockersModal";

interface AuditStatusSplitButtonProps {
  audit: UIAudit;
  onActionComplete?: () => void;
}

export function AuditStatusSplitButton({
  audit,
  onActionComplete,
}: AuditStatusSplitButtonProps) {
  const { t } = useTranslation(["audits"]);
  const statusLabels = useAuditStatusLabels();
  const transitionLabels = useAuditTransitionLabels();
  const transitionConfig = AUDIT_TRANSITION_CONFIG[audit.status];

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [blockersOpen, setBlockersOpen] = useState(false);
  const [validationReport, setValidationReport] =
    useState<ValidationReport | null>(null);
  const [isValidating, setIsValidating] = useState(false);

  const { execute, validateReadiness, isPending } = useAuditStatusChange();

  if (!transitionConfig) {
    // Terminal state (archived) — no button
    return null;
  }

  // Capture narrowed value for use in nested functions
  const config = transitionConfig;

  const primaryAction = getActionForTransition(
    audit.status,
    config.targetStatus,
  );
  const targetStatusConfig = statusLabels[config.targetStatus];

  async function handlePrimaryClick() {
    // For submit action, validate readiness first
    if (primaryAction === "submit") {
      setIsValidating(true);
      try {
        const result = await validateReadiness(audit.id as Id<"audits">);
        setIsValidating(false);

        // useApiMutation unwraps the Result: result is the raw data
        // { isReady: boolean, validationReport: ValidationReport }
        if (!result.isReady) {
          setValidationReport(result.validationReport as ValidationReport);
          setBlockersOpen(true);
        } else {
          // Proceed to confirm modal
          setConfirmOpen(true);
        }
      } catch (_error) {
        setIsValidating(false);
        toast.error(t("audits:actions.submitBlockers.validationError"));
      }
      return;
    }

    // Non-submit actions: proceed as before
    if (config.requiresReason) {
      setRejectOpen(true);
    } else {
      setConfirmOpen(true);
    }
  }

  async function handleConfirm() {
    if (!primaryAction) return;
    await execute(
      primaryAction,
      audit.id as Id<"audits">,
      primaryAction === "move_back"
        ? { targetStatus: config.targetStatus }
        : undefined,
    );
    setConfirmOpen(false);
    onActionComplete?.();
  }

  async function handleRejectConfirm(reason: string) {
    await execute("reject", audit.id as Id<"audits">, { reason });
    setRejectOpen(false);
    onActionComplete?.();
  }

  const isLoading = isPending || isValidating;

  return (
    <>
      <div className="flex items-center">
        {/* Main action button */}
        <button
          type="button"
          onClick={handlePrimaryClick}
          disabled={isLoading}
          className="inline-flex items-center gap-1.5 rounded-l-button px-3 h-[34px] text-sm font-medium border-r-0 border transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          style={{
            backgroundColor: targetStatusConfig.badgeBg,
            color: targetStatusConfig.badgeText,
            borderColor: "transparent",
          }}
        >
          {t(`audits:transition.${audit.status}`)}
        </button>

        {/* Arrow trigger for dropdown */}
        <Dropdown>
          <Dropdown.Trigger>
            <button
              type="button"
              disabled={isLoading}
              className="inline-flex items-center justify-center rounded-r-button px-2 h-[34px] border-l border transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              style={{
                backgroundColor: targetStatusConfig.badgeBg,
                color: targetStatusConfig.badgeText,
                borderColor: "transparent",
                borderLeftColor: `${targetStatusConfig.badgeText}33`,
              }}
            >
              <ChevronDown size={14} />
            </button>
          </Dropdown.Trigger>
          <Dropdown.Content align="end">
            {/* Primary action repeated */}
            <Dropdown.Item onSelect={handlePrimaryClick} disabled={isPending}>
              {t(`audits:transition.${audit.status}`)}
            </Dropdown.Item>

            <Dropdown.Separator />

            {/* Rollback / secondary actions */}
            {(() => {
              const rollbacks = [];
              for (const [key, value] of Object.entries(transitionLabels)) {
                const [source, target] = key.split("->") as [
                  typeof audit.status,
                  typeof audit.status,
                ];
                if (source !== audit.status) continue;
                // Skip the primary forward transition
                if (target === config.targetStatus) continue;
                rollbacks.push({ key, targetStatus: target, ...value });
              }
              return rollbacks.map((rb) => (
                <Dropdown.Item
                  key={rb.key}
                  disabled={isPending}
                  onSelect={async () => {
                    if (rb.action === "reject") {
                      setRejectOpen(true);
                    } else {
                      await execute(rb.action, audit.id as Id<"audits">, {
                        targetStatus: rb.targetStatus,
                      });
                      onActionComplete?.();
                    }
                  }}
                >
                  {rb.label}
                </Dropdown.Item>
              ));
            })()}
          </Dropdown.Content>
        </Dropdown>
      </div>

      {/* Confirmation modals (mounted here — internal to this component) */}
      <AuditStatusConfirmModal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirm}
        actionLabel={t(`audits:transition.${audit.status}`)}
        targetStatusLabel={t(`audits:status.${config.targetStatus}`)}
        isPending={isPending}
      />

      <AuditRejectModal
        open={rejectOpen}
        onClose={() => setRejectOpen(false)}
        onConfirm={handleRejectConfirm}
        isPending={isPending}
      />

      {validationReport && (
        <AuditSubmissionBlockersModal
          open={blockersOpen}
          onClose={() => setBlockersOpen(false)}
          validationReport={validationReport}
        />
      )}
    </>
  );
}
